import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCapIcon, BriefcaseIcon, WrenchIcon, SparklesIcon } from 'lucide-react';
interface Player {
  id: string;
  name: string;
  color: string;
  pathIndex: number | null;
  avatar?: string | null;
}
interface JourneyStartSceneProps {
  players: Player[];
  onStartPlaying: () => void;
}
const DAY_CITY_IMAGE = 'https://cdn.magicpatterns.com/uploads/bnfxUB7vnymHPU6h8qdKoF/ChatGPT_Image_May_5,_2026,_12_21_41_PM.png';
const PATH_COLORS = [{
  stroke: '#8b5cf6',
  fill: '#a78bfa',
  name: 'School',
  emoji: '🎓',
  icon: GraduationCapIcon
}, {
  stroke: '#ec4899',
  fill: '#f472b6',
  name: 'Workforce',
  emoji: '💼',
  icon: BriefcaseIcon
}, {
  stroke: '#f59e0b',
  fill: '#fbbf24',
  name: 'Trade',
  emoji: '🔧',
  icon: WrenchIcon
}, {
  stroke: '#14b8a6',
  fill: '#2dd4bf',
  name: 'Self-Made',
  emoji: '✨',
  icon: SparklesIcon
}];
export function JourneyStartScene({
  players,
  onStartPlaying
}: JourneyStartSceneProps) {
  // Group players by their chosen path
  const playersByPath: Record<number, Player[]> = {
    0: [],
    1: [],
    2: [],
    3: []
  };
  players.forEach(p => {
    const idx = p.pathIndex !== null && p.pathIndex !== undefined ? Math.max(0, Math.min(3, p.pathIndex)) : 0;
    playersByPath[idx].push(p);
  });
  return <div className="fixed inset-0 w-full h-full z-30 overflow-hidden bg-black" data-id="element-256">
      {/* DAY CITY BACKDROP — full bleed realistic 3D scene */}
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: `url('${DAY_CITY_IMAGE}')`
    }} data-id="element-257" />

      {/* Soft top gradient so the title is readable */}
      <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-black/40 via-black/10 to-transparent pointer-events-none" data-id="element-258" />

      {/* Top title */}
      <div className="absolute top-0 left-0 right-0 flex flex-col items-center pt-6 pb-4 pointer-events-none z-10" data-id="element-259">
        <div className="flex items-center gap-3 mb-2" data-id="element-260">
          <span className="text-3xl drop-shadow-lg" data-id="element-261">✨</span>
          <h1 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-400 to-yellow-400 drop-shadow-[0_4px_20px_rgba(0,0,0,0.6)] text-center" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-262">
            Your Journey Begins!
          </h1>
          <span className="text-3xl drop-shadow-lg" data-id="element-263">✨</span>
        </div>
        <p className="text-xs md:text-sm text-white font-bold tracking-widest uppercase bg-black/40 px-6 py-1.5 rounded-full backdrop-blur-sm text-center" data-id="element-264">
          Follow your path. Make your choices. Build your life.
        </p>
      </div>

      {/* Player pawns floating along the bottom of the road */}
      <div className="absolute bottom-32 left-0 right-0 flex justify-center gap-6 md:gap-12 pointer-events-none z-10 px-4" data-id="element-265">
        {[0, 1, 2, 3].map(pathIdx => {
        const pathPlayers = playersByPath[pathIdx];
        const pathColor = PATH_COLORS[pathIdx];
        const Icon = pathColor.icon;
        if (pathPlayers.length === 0) return null;
        return <div key={pathIdx} className="flex flex-col items-center gap-1.5" data-id="element-266">
              <div className="flex gap-1.5 flex-wrap justify-center max-w-[140px]" data-id="element-267">
                {pathPlayers.map((p, idx) => <motion.div key={p.id} initial={{
              y: -20,
              opacity: 0
            }} animate={{
              y: [0, -8, 0],
              opacity: 1
            }} transition={{
              y: {
                duration: 1.8,
                repeat: Infinity,
                delay: idx * 0.2
              },
              opacity: {
                duration: 0.4,
                delay: idx * 0.1
              }
            }} className="flex flex-col items-center" data-id="element-268">
                    <div className="rounded-full border-[3px] border-white shadow-2xl overflow-hidden flex items-center justify-center" style={{
                width: 44,
                height: 44,
                background: pathColor.fill,
                boxShadow: `0 0 24px ${pathColor.stroke}, 0 6px 12px rgba(0,0,0,0.5)`
              }} data-id="element-269">
                      {p.avatar ? <img src={p.avatar} alt="" className="w-full h-full object-cover" data-id="element-270" /> : <span className="text-white font-bold text-base" data-id="element-271">
                          {p.name.charAt(0).toUpperCase()}
                        </span>}
                    </div>
                    <div className="mt-1 px-2 py-0.5 rounded-full text-[9px] font-bold text-white whitespace-nowrap shadow-lg" style={{
                background: pathColor.stroke
              }} data-id="element-272">
                      {p.name}
                    </div>
                  </motion.div>)}
              </div>
              {/* Path label */}
              <div className="px-3 py-1 rounded-full text-[10px] font-bold text-white shadow-lg flex items-center gap-1.5" style={{
            background: pathColor.stroke,
            boxShadow: `0 0 16px ${pathColor.stroke}80`
          }} data-id="element-273">
                <Icon className="w-3 h-3" data-id="element-274" />
                {pathColor.name}
              </div>
            </div>;
      })}
      </div>

      {/* Roll Dice CTA */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center z-20" data-id="element-275">
        <motion.button onClick={onStartPlaying} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.97
      }} className="group flex items-center gap-4 bg-gradient-to-r from-purple-600 to-pink-600 backdrop-blur-md p-2 pr-8 rounded-full border-2 border-white shadow-[0_0_40px_rgba(236,72,153,0.8)] hover:shadow-[0_0_60px_rgba(236,72,153,1)] transition-all" data-id="element-276">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-inner" data-id="element-277">
            <motion.span className="text-4xl" animate={{
            rotate: [0, 360]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-278">
              🎲
            </motion.span>
          </div>
          <div className="flex flex-col items-start" data-id="element-279">
            <span className="text-white font-bold text-lg uppercase tracking-wider" data-id="element-280">
              Roll the dice
            </span>
            <span className="text-pink-100 text-sm" data-id="element-281">
              to start your journey!
            </span>
          </div>
        </motion.button>
      </div>
    </div>;
}